/**
 * InvitesTab
 *
 * Organism that renders the Invitations table and
 * the filter drawer. Delegates all state to `useInviteTab`.
 *
 * ## Component Hierarchy
 * - **Level**: Organism
 * - **Feature**: Users
 *
 * @module features/users/components/ui/organisms/InvitesTab
 */
import React, { useMemo } from 'react';
import { Button, Skeleton, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import { useLanguage } from '@/shared/hooks/useLanguage';
import { PAGE_SIZE_OPTIONS } from '@/features/users/constants/pagination';
import { getInviteStatusTag } from '@/features/users/utils/statusTags';
import { getSkeletonTableProps } from '@/features/users/utils/tableSkeleton';
import { InviteFiltersDrawer } from '@/features/users/components/ui/molecules/InviteFiltersDrawer';
import type { useInviteTab } from '@/features/users/hooks/useInviteTab';
import type {
  InviteItem,
  InviteStatus,
} from '@/features/users/types/users.types';

import { TableEmptyState } from '@/features/users/components/ui/molecules/TableEmptyState';

import './InvitesTab.css';

export interface InvitesTabProps {
  /** All state and handlers coming from useInviteTab */
  tab: ReturnType<typeof useInviteTab>;
}

/**
 * InvitesTab Component
 *
 * @param props - Component properties
 * @returns Table with invite rows and filter drawer
 *
 * @example
 * ```tsx
 * const tab = useInviteTab();
 * <InvitesTab tab={tab} />
 * ```
 */
export const InvitesTab: React.FC<InvitesTabProps> = ({ tab }) => {
  const { t } = useLanguage();

  const columns: ColumnsType<InviteItem> = useMemo(
    () => [
      {
        title: t('Email'),
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: t('Status'),
        dataIndex: 'status',
        key: 'status',
        render: (status: InviteStatus) => getInviteStatusTag(status, t),
      },
      {
        title: t('Expiration date'),
        dataIndex: 'expiresAt',
        key: 'expiresAt',
        render: (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm'),
      },
      {
        title: t('Actions'),
        key: 'actions',
        align: 'right',
        render: (_, record) => {
          if (record.status === 'PENDING') {
            return (
              <Button
                type="link"
                danger
                onClick={() => tab.handleCancelInvite(record)}
              >
                {t('Cancel')}
              </Button>
            );
          }
          if (record.status === 'EXPIRED') {
            return (
              <Button
                type="link"
                onClick={() => tab.handleResendInvite(record)}
              >
                {t('Resend')}
              </Button>
            );
          }
          return null;
        },
      },
    ],
    [tab, t]
  );

  // While (re)loading, render skeleton rows so the table looks refreshed.
  const isTableLoading = tab.isLoading || tab.isRefreshing;
  const rows = tab.invitesData?.content ?? [];
  const totalInvites = tab.invitesData?.content.length ?? 0;
  const skeleton = getSkeletonTableProps<InviteItem>(columns, rows.length);

  return (
    <>
      <div className="idf-data-table">
        {isTableLoading ? (
          <Skeleton.Input
            active
            size="small"
            className="idf-data-table__length-skeleton"
          />
        ) : (
          <p className="idf-data-table__length">
            {t('{{count}} invites found', { count: totalInvites })}
          </p>
        )}
        <Table<InviteItem>
          columns={isTableLoading ? skeleton.columns : columns}
          dataSource={isTableLoading ? skeleton.dataSource : rows}
          rowKey={isTableLoading ? skeleton.rowKey : 'id'}
          pagination={{
            current: tab.page + 1,
            pageSize: tab.pageSize,
            total: tab.invitesData?.page?.totalElements ?? 0,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS as unknown as string[],
            onChange: (nextPage, nextPageSize) => {
              tab.setPage(nextPage - 1);
              if (nextPageSize) tab.setPageSize(nextPageSize);
            },
          }}
          locale={{
            emptyText: (
              <TableEmptyState
                title={t('No invites')}
                description={t('No invites were found')}
              />
            ),
          }}
        />
      </div>

      <InviteFiltersDrawer
        open={tab.filtersOpen}
        draftFilters={tab.draftFilters}
        onDraftChange={tab.setDraftFilters}
        onClose={tab.handleCloseFilters}
        onClear={tab.handleClearFilters}
        onApply={tab.handleApplyFilters}
      />
    </>
  );
};

export default InvitesTab;
